/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Stack, TextArea, TextInput } from '@carbon/react';
import { createPortal } from 'react-dom';

import { notificationStore } from 'stores';
import { milestoneService } from 'services';
import { isTemplate } from 'utils/helpers';

import * as Styled from './VersionCreationModal.styled';
import { createVersion, createVersionWithRolesCheck } from './create-version';

const CONNECTOR_TEMPLATE_TYPE = 'connector_template';
const DIAGRAM_TYPE = 'diagram';

export const LABELS = {
  diagram: {
    title: () => 'Create a new version',
    description: () => 'After you create this version, it will be available in your versions list.',
    input: 'Please name this version (optional):',
    default: 'Untitled version',
    button: 'Create'
  },
  connector_template: {
    title: (targetIsOrganization) => `Publish version to ${targetIsOrganization ? 'organization' : 'project'}`,
    description: (targetIsOrganization, templateName) =>
      `Publish "${templateName}"
      ${
        targetIsOrganization
          ? 'to the organization to allow members'
          : 'to enable it in your project and allow collaborators'
      }
      to use it in their diagrams.`,
    input: 'Version name (optional):',
    default: 'Untitled version',
    button: 'Publish'
  }
};

export const VersionCreationModal = ({ onClose, onSuccess, open, file, origin, targetIsOrganization }) => {
  const [value, setValue] = useState('');
  const [version, setVersion] = useState(1);
  const [description, setDescription] = useState('');
  const type = isTemplate(file) ? CONNECTOR_TEMPLATE_TYPE : DIAGRAM_TYPE;
  const defaultName = `${LABELS[type].default} ${version}`;

  useEffect(() => {
    if (open) {
      milestoneService
        .fetchByFileId(file.id)
        .then((milestones) => {
          setVersion(milestones.length + 1);
        })
        .catch(() => notificationStore.showError('Could not fetch your versions. Please try again later.'));
    }
  }, [open, file]);

  const handleClose = () => {
    setValue('');
    setDescription('');
    onClose(false);
  };

  const handleConfirm = async () => {
    const payload = {
      file: file,
      name: value || defaultName,
      description,
      origin: origin,
      organizationPublic: targetIsOrganization,
      onSuccess
    };

    const result = type === CONNECTOR_TEMPLATE_TYPE ? createVersionWithRolesCheck(payload) : createVersion(payload);

    result
      .then(() => {
        notificationStore.showSuccess(
          isTemplate(file) ? `"${file.name}" has been published.` : 'New version added to versions list.'
        );

        setValue('');
        setDescription('');
        onClose();
      })
      .catch(() => {
        notificationStore.showError(
          isTemplate(file)
            ? `Yikes! Could not publish "${file.name}". Please try again later.`
            : 'Yikes! Could not create a new version. Please try again later.'
        );
      });
  };

  return createPortal(
    <Styled.VersionCreationModal
      open={open}
      modalHeading={LABELS[type].title(targetIsOrganization)}
      primaryButtonText={LABELS[type].button}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleConfirm}
      onRequestClose={handleClose}
      preventCloseOnClickOutside
    >
      <Stack gap={4}>
        <span>{LABELS[type].description(targetIsOrganization, file.name)}</span>

        <TextInput
          data-modal-primary-focus
          data-test="version-name"
          id="version-name"
          aria-labelledby="version-name-label"
          labelText={LABELS[type].input}
          placeholder={defaultName}
          maxCount={255}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          autoComplete="off"
        />

        <TextArea
          id="version-description"
          labelText="Version description"
          placeholder="What changed from the previous version?"
          enableCounter
          counterMode="character"
          maxCount={255}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
          autoComplete="off"
        />
      </Stack>
    </Styled.VersionCreationModal>,
    document.body
  );
};

VersionCreationModal.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  open: PropTypes.bool.isRequired,
  file: PropTypes.object.isRequired,
  origin: PropTypes.string
};
